import { Injectable } from '@angular/core';

import { initializeApp, FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { Auth, getAuth } from 'firebase/auth';
import { Firestore, getFirestore, collection, query, where, getDocs, DocumentData, QueryDocumentSnapshot, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { Functions, getFunctions } from 'firebase/functions';
import { Analytics, getAnalytics } from 'firebase/analytics';
import { FirebaseStorage, getStorage } from 'firebase/storage';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {

  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly firestore: Firestore;
  readonly functions: Functions;
  readonly analytics: Analytics;
  readonly storage: FirebaseStorage;

  constructor() {
    this.app = initializeApp(environment.firebaseConfig);
    // App Check (reCAPTCHA Enterprise) — attaches a bot-protection token so the
    // backend can verify calls originate from this app. Initialized before auth/
    // firestore/functions so tokens attach to their requests. Skipped when no
    // site key is set (staging).
    if (environment.appCheckSiteKey) {
      initializeAppCheck(this.app, {
        provider: new ReCaptchaEnterpriseProvider(environment.appCheckSiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    }
    this.auth = getAuth(this.app);
    this.firestore = getFirestore(this.app);
    this.functions = getFunctions(this.app, environment.firebaseConfig.functionsRegion);
    this.analytics = getAnalytics(this.app);
    this.storage = getStorage(this.app);
  }

  getCollection(path: string): Observable<DocumentData[]> {
    const collectionRef = collection(this.firestore, path);
    return from(getDocs(collectionRef)).pipe(
      map(snapshot => snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({ id: doc.id, ...doc.data() })))
    );
  }

  async getCollectionWithQuery(path: string, field: string, operator: any, value: any): Promise<DocumentData[]> {
    const collectionRef = collection(this.firestore, path);
    const q = query(collectionRef, where(field, operator, value));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({ id: doc.id, ...doc.data() }));
  }

  async addDocument(path: string, data: any, id?: string): Promise<void> {
    const docRef = id ? doc(this.firestore, path, id) : doc(this.firestore, path);
    await setDoc(docRef, data);
  }

  async updateDocument(path: string, id: string, data: any): Promise<void> {
    const docRef = doc(this.firestore, path, id);
    await updateDoc(docRef, data);
  }

  async deleteDocument(path: string, id: string): Promise<void> {
    const docRef = doc(this.firestore, path, id);
    await deleteDoc(docRef);
  }
}
